/* eslint-disable */

;(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined'
    ? (module.exports = factory())
    : typeof define === 'function' && define.amd
      ? define(factory)
      : ((global = typeof globalThis !== 'undefined' ? globalThis : global || self), (global.EmbedRenderer = factory()))
})(this, function () {
  'use strict'

  /**
   * oembed.utils.mjs - Common helper functions shared across modules.
   *
   * @package oembed
   * @copyright 2025 RTL Technology GmbH
   */

  /**
   * Deep-merge one object into another.
   *
   * - Merges source keys into target, overwriting existing fields.
   * - Recursively merges plain objects.
   * - Arrays are replaced outright.
   */
  function deepMerge(target, source) {
    if (typeof target !== 'object' || target === null) {
      return source
    }
    if (typeof source !== 'object' || source === null) {
      return target
    }
    for (const key of Object.keys(source)) {
      const value = source[key]
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        if (typeof target[key] !== 'object' || target[key] === null || Array.isArray(target[key])) {
          target[key] = {}
        }
        deepMerge(target[key], value)
      } else {
        target[key] = value
      }
    }
    return target
  }

  /**
   * Check if a string is a valid URL using `URL.canParse()` if available.
   * Fallback to a try/catch if not available.
   */
  function isValidUrl(url) {
    if (!url) {
      return false
    }
    if ('canParse' in URL) {
      return URL.canParse(url)
    }
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  /**
   * Safely escape HTML for insertion into a document.
   */
  function escapeHtml(str) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  }

  /**
   *
   * @copyright 2025 RTL Technology GmbH
   */
  const EmbedRenderer = (function (global) {
    let _config = {
      debugMode: false,
      safeMode: false,
      consentHandling: false,
    }

    // All Embed instances.
    const instances = []

    // Event listeners keyed by event type.
    const listeners = {
      render: [],
      consent: [],
      load: [],
      resize: [],
      draw: [],
      error: [],
    }

    /**
     *
     */
    function configure(config) {
      deepMerge(_config, config)
    }

    /**
     * Registers a plugin, hooking up any defined event listeners.
     */
    function plugin(plugin) {
      if (typeof plugin.onRender === 'function') {
        addEventListener('render', plugin.onRender.bind(plugin))
      }
      if (typeof plugin.onConsent === 'function') {
        addEventListener('consent', plugin.onConsent.bind(plugin))
      }
      if (typeof plugin.onLoad === 'function') {
        addEventListener('load', plugin.onLoad.bind(plugin))
      }
      if (typeof plugin.onResize === 'function') {
        addEventListener('resize', plugin.onResize.bind(plugin))
      }
      if (typeof plugin.onDraw === 'function') {
        addEventListener('draw', plugin.onDraw.bind(plugin))
      }
      if (typeof plugin.onError === 'function') {
        addEventListener('error', plugin.onError.bind(plugin))
      }
    }

    /**
     * Add an event listener for a specific event type.
     *
     * @param {String} name
     * @param {Function} handler
     * @throws If the given event is not known
     */
    function addEventListener(name, handler) {
      if (!listeners[name]) {
        throw new Error(`Unknown event type "${name}"`)
      }
      listeners[name].push(handler)
    }

    /**
     * Dispatch an event to all registered listeners for that event type.
     *
     * @param {String} name
     * @param {Object} payload
     */
    async function dispatchEvent(name, payload) {
      if (listeners[name]) {
        for (const handler of listeners[name]) {
          await handler(payload)
        }
      }
    }

    /**
     * Creates an embed instance from an HTMLElement and renders the embed into
     * this element.
     *
     * @param {HTMLElement} el
     * @returns {Promise<Embed>}
     */
    async function mount(el) {
      const url = el.getAttribute('data-url')
      if (!isValidUrl(url)) {
        throw new Error('Cannot parse embed url: ' + url)
      }
      const embed = new EmbedRenderer.Embed(url, {
        consentHandling:
          (_config.consentHandling && !el.hasAttribute('data-no-consent')) ||
          el.getAttribute('data-no-consent') !== 'true',
        safeMode:
          (_config.safeMode && !el.hasAttribute('data-safe-mode')) || el.getAttribute('data-safe-mode') === 'true',
      })
      if (el.getAttribute('data-params')) {
        embed.params = el.getAttribute('data-params')
      }
      await embed.mount(el)
    }

    /**
     * Mount multiple DOM elements (NodeList, Array, etc.).
     */
    async function mountAll(els) {
      for (const el of els) {
        await mount(el)
      }
    }

    /**
     * Helper to create the container element for an embed.
     */
    function createContainer(embed) {
      const container = embed.el.ownerDocument.createElement('div')
      container.style.position = 'relative'
      container.style.textAlign = 'center'
      //container.style.minWidth = embed.el.scrollWidth + "px";
      //container.style.minHeight = embed.el.scrollHeight + "px";
      container.style.overflow = 'hidden'
      return container
    }

    /**
     * Helper to create the iframe (sandbox) for rendering the embed.
     */
    function createSandbox(embed) {
      const sandbox = embed.el.ownerDocument.createElement('iframe')
      const embedTitle = embed.data?.title || embed.el.getAttribute('title') || ''
      sandbox.setAttribute('title', embedTitle)
      //sandbox.setAttribute("width", embed.data?.width || "100%");
      //sandbox.setAttribute("height", embed.data?.height || "100%");
      sandbox.setAttribute('width', '100%')
      sandbox.setAttribute('height', '100%')
      sandbox.setAttribute('src', 'about:blank')
      sandbox.setAttribute('style', 'border:0;overflow:hidden;margin:0 auto')
      sandbox.setAttribute('loading', 'eager')
      sandbox.setAttribute('referrerpolicy', 'no-referrer')
      sandbox.setAttribute('framespacing', '0')
      sandbox.setAttribute('allow', "geolocation 'none'; fullscreen 'none'; payment 'none'; autoplay 'none'")
      sandbox.setAttribute('credentialless', 'credentialless')
      return sandbox
    }

    /**
     * Base Plugin class.
     * Extend this to create your own plugin with any of the below event methods.
     */
    class Plugin {
      async onRender(_ref) {
        let { embed } = _ref
      }
      async onConsent(_ref2) {
        let { embed } = _ref2
      }
      async onLoad(_ref3) {
        let { embed } = _ref3
      }
      async onResize(_ref4) {
        let { embed, rect } = _ref4
      }
      async onDraw(_ref5) {
        let { embed, sandbox } = _ref5
      }
      async onError(_ref6) {
        let { embed, error, status } = _ref6
      }
    }

    /**
     * The core Embed class.
     */
    class Embed {
      /**
       *
       * @param {string} url
       */
      constructor(url, config) {
        instances.push(this)
        this.id = instances.length
        this.url = url
        this.cfg = {}
        deepMerge(this.cfg, _config)
        deepMerge(this.cfg, config)
        this.loaded = false
        this.mounted = false
        this.params = ''
      }

      /**
       * Construct the proxy URL for retrieving embed data.
       */
      getProxyUrl() {
        const url = new URL(this.cfg.proxyUrl)
        url.searchParams.append('url', this.url)
        url.searchParams.append('dnt', this.cfg.consentHandling)
        //url.searchParams.append("max-width", this.el.clientWidth);
        url.searchParams.append('omit-script', this.cfg.safeMode)
        if (this.params !== '') {
          url.search += '&' + this.params
        }
        return url
      }

      /**
       * Fetch embed data from the proxy. Only fetches once per instance.
       */
      async fetch() {
        if (!this.loaded) {
          try {
            const resp = await global.fetch(this.getProxyUrl(), {
              /*headers: {
                  'Accept': 'application/json',
                  'X-Api-Key': _config.proxyApiKey
              }*/
            })
            if (!resp.ok) {
              const error = new Error(`Failed to load embed data (HTTP ${resp.status}).`)
              await dispatchEvent('error', {
                embed: this,
                error,
                status: resp.status,
              })
              return
            }
            this.data = await resp.json()
            this.loaded = true
            await dispatchEvent('load', {
              embed: this,
            })
          } catch (e) {
            await dispatchEvent('error', {
              embed: this,
              error: e,
              status: 0,
            })
          }
        }
      }

      /**
       * Render the embed into its container (iframe sandbox).
       */
      async render() {
        if (this.loaded && this.mounted) {
          // fix for youtube error 153 in ios webview
          // doesn't work with sandbox

          if (this.data.provider_name === 'YouTube' && document.location.hostname === 'apps.n-tv.de') {
            //this.data.html = this.data.html.replace(/\s+referrerpolicy="[^"]*"/g, '');
            this.el.style.aspectRatio = '16 / 9'
            const parser = new DOMParser()
            const doc = parser.parseFromString(this.data.html, 'text/html')
            const element = doc.body.firstChild
            element.style.width = '100%'
            element.style.height = '100%'
            this.el.replaceChildren(element)
            return
          }

          // makes flourisch responsive
          if (this.data.provider_name === 'Flourish') {
            const matchUrl = this.data.html.match(/src="([^"]+)"/)

            if (matchUrl[0] && matchUrl[1]) {
              try {
                const url = new URL(matchUrl[1])
                url.searchParams.append('auto', '1')
                this.data.html = this.data.html.replace(matchUrl[1], url)
              } catch (e) {
                console.error(e)
              }
            }
          }

          if (this.data.provider_name === 'Heimspiel' && document.location.hostname === 'apps.n-tv.de') {
            this.data.html = this.data.html.replace(
              'https://sportdaten.n-tv.de/iframe/',
              'https://sportdaten.n-tv.de/app/iframe/',
            )
          }

          // makes facebook responsive
          if (this.data.provider_name === 'Facebook') {
            /*
            post:   https://beta.n-tv.de/leute/Saenger-der-Bollock-Brothers-stirbt-bei-Badeunfall-article25933996.html
            video:  https://beta.n-tv.de/leute/ALS-Diagnose-soll-fuer-Eric-Dane-nicht-mein-Ende-sein-article25831643.html
            foto:   https://beta.n-tv.de/panorama/Lottogewinner-landet-nach-monatelangen-Partys-im-Krankenhaus-article26075980.html
            */
            const matchWidth = this.data.html.match(/data-width="([^"]+)"/)
            const matchHref = this.data.html.match(/data-href="([^"]+)"/)

            if (matchHref[0] && matchHref[1] && matchHref[1].includes('/videos/')) {
              this.data.html = this.data.html.replace('fb-post', 'fb-video')
            }

            if (matchWidth[0]) {
              try {
                this.data.html = this.data.html.replace(matchWidth[0], 'data-width="auto"')
              } catch (e) {
                console.error(e)
              }
            }
          }

          const container = createContainer(this)
          const sandbox = createSandbox(this)
          container.appendChild(sandbox)
          this.el.replaceChildren(container)
          //this.el.appendChild(container);

          const doc = sandbox.contentDocument || sandbox.contentWindow.document
          const embedTitle = this.data?.title || this.el.getAttribute('title') || ''
          doc.open()
          doc.write(`
                  <!DOCTYPE html>
                  <html role="presentation">
                    <head>
                      <title>${escapeHtml(embedTitle)}</title>
                      <style>
                        html, body {
                          height: auto;
                          width: 100%;
                          margin: 0;
                          padding: 0;
                          overflow: hidden;
                          ${this.data.provider_name !== 'Facebook' ? 'display: flex;' : ''}
                          ${this.data.provider_name === 'YouTube' ? 'aspect-ratio: 16 / 9;' : ''}
                          justify-content: center;
                        }
                        body > * {
                          margin:0 auto;
                          max-width:100%;
                          width: 100%;
                          box-sizing: border-box;
                        }
                        ${this.data.provider_name === 'YouTube' ? 'body iframe { height: 100%; }' : ''}
                      </style>
                      ${
                        this.data.provider_name === 'Flourish'
                          ? `
                      <script>
                        window.addEventListener("message", function(event) {
                          if(event.data){
                            try{
                              const data = JSON.parse(event.data);
                              if (data && data.context == "iframe.resize" && data.height) {
                                document.getElementsByTagName("iframe")[0].style.height = data.height + 'px';
                              }
                            }catch(e){
                              console.error(e);
                            }
                          }
                        });
                      </script>`
                          : ''
                      }
                    </head>
                    <body role="presentation">
                      ${this.data?.html || ''}
                    </body>
                  </html>
                `)
          doc.close()
          await dispatchEvent('draw', {
            embed: this,
            sandbox,
          })
          const resizeObserver = new ResizeObserver(async (entries) => {
            sandbox.contentWindow.requestAnimationFrame(async () => {
              if (!Array.isArray(entries) || !entries.length) {
                return
              }
              sandbox.setAttribute('height', entries[0].contentRect.height)
              await dispatchEvent('resize', {
                embed: this,
                rect: entries[0].contentRect,
              })
            })
          })
          sandbox.contentWindow.addEventListener('unload', () => resizeObserver.disconnect())
          resizeObserver.observe(doc.body)
        }
      }

      /**
       * Mount the embed into the given DOM element and dispatch "render".
       */
      async mount(el) {
        this.el = el
        this.mounted = true
        await dispatchEvent('render', {
          embed: this,
        })

        // If no consent is needed, proceed to fetch + render immediately.
        if (!this.cfg.consentHandling) {
          await this.consent()
        }
      }

      /**
       * Called when a user explicitly gives consent (or is auto-called if no consent is needed).
       */
      async consent() {
        await dispatchEvent('consent', {
          embed: this,
        })
        await this.fetch()
        await this.render()
      }
    }
    return {
      instances,
      configure,
      plugin,
      mount,
      mountAll,
      addEventListener,
      Embed,
      Plugin,
    }
  })(globalThis)

  /**
   * LoggerPlugin.js - A plugin that logs various lifecycle events for debugging.
   */
  class LoggerPlugin extends EmbedRenderer.Plugin {
    async onRender(_ref) {
      let { embed } = _ref
      console.debug(`[oembed:${embed.id}] Embed mounted, awaiting consent.`, {
        embed,
      })
    }
    async onConsent(_ref2) {
      let { embed } = _ref2
      console.debug(`[oembed:${embed.id}] Consent given.`, {
        embed,
      })
    }
    async onLoad(_ref3) {
      let { embed } = _ref3
      console.debug(`[oembed:${embed.id}] Content loaded from ${embed.getProxyUrl()}`, {
        embed,
      })
    }
    async onDraw(_ref4) {
      let { embed } = _ref4
      console.debug(`[oembed:${embed.id}] Embed rendered successfully.`, {
        embed,
      })
    }
    async onResize(_ref5) {
      let { embed, rect } = _ref5
      console.debug(`[oembed:${embed.id}] Resized embed to ${rect.height}px in height.`, {
        embed,
      })
    }
    async onError(_ref6) {
      let { embed, error } = _ref6
      console.error(`[oembed:${embed.id}] ${error.message}`, {
        embed,
      })
    }
  }

  /**
   * ConsentPlugin.js
   * A plugin that waits for a button click within the embed element, then calls `embed.consent()`.
   */
  class ConsentPlugin extends EmbedRenderer.Plugin {
    async onRender(_ref) {
      let { embed } = _ref
      const button = embed.el.querySelector('button')
      if (button) {
        button.addEventListener('click', () => {
          embed.consent()
        })
      }
    }
  }

  /**
   * ErrorHandler.js
   * A plugin that waits for a button click within the embed element, then calls `embed.consent()`.
   */
  class ErrorHandlerPlugin extends EmbedRenderer.Plugin {
    constructor(config) {
      super()
      this.config = config
    }
    async onConsent(_ref) {
      let { embed } = _ref
      embed.el.classList.add('oembed-loading')
    }
    async onDraw(_ref2) {
      let { embed, sandbox } = _ref2
      if (this.config.loadingIndicatorDelay) {
        sandbox.contentWindow.setTimeout(function () {
          embed.el.classList.remove('oembed-loading')
        }, this.config.loadingIndicatorDelay)
      } else {
        embed.el.classList.remove('oembed-loading')
      }
    }
    async onError(_ref3) {
      let { embed, error, status } = _ref3
      embed.el.classList.add('oembed-error')
    }
  }

  EmbedRenderer.ConsentPlugin = ConsentPlugin
  EmbedRenderer.LoggerPlugin = LoggerPlugin
  EmbedRenderer.ErrorHandlerPlugin = ErrorHandlerPlugin

  return EmbedRenderer
})
//# sourceMappingURL=oembed.js.map
